import {memo, useEffect, useMemo} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import {useSuiContext} from '@s-ui/react-context'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import SpinnerBasic, {spinnerBasicOverlayTypes, spinnerBasicTypes} from '@adv-mt/mt-spinner-basic'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'
import {Button, BUTTON_SHAPES, BUTTON_SIZES, BUTTON_VARIANTS} from '@adv-mt/ui/button'
import {LOADING, MediaImage, OBJECT_FIT} from '@adv-mt/ui/media-image'
import {Body1, Body2, Caption, Headline1} from '@adv-mt/ui/typography'

import {DRAWER_TYPES} from '../../drawers/config.js'
import {scrollToSection} from '../../utils.js'
import FinancingCalculator from './calculator/FinancingCalculator.js'
import FinancingClaims from './claims/FinancingClaims.js'
import FinancingDetails from './details/FinancingDetails.js'
import {useFinancingCalculations} from './hooks/useFinancingCalculations.js'
import {BASE_CLASS, FARE_TYPES, FINANCING_STATUS} from './config.js'
import {FINANCING_STRATEGIES} from './strategies.js'
import {getDetailRows, resolveFinancingMode} from './utils.js'

const FinancingPanel = ({
  ad = {},
  seller = {},
  financingData,
  onContactSellerCtaClick,
  onFinancingDataChange,
  onOpenDrawer,
  showC2cFinancing = false,
  showB2cFinancingPanel = false,
  vehicleInfo = {}
}) => {
  const {domain, i18n} = useSuiContext()
  const config = domain.get('config')
  const {track} = useAnalyticsTrack()

  const isHigherThanMobile = useMediaQuery(useMediaQueryBreakpoints.sm, {
    defaultMatches: !config.get('isMobile')
  })
  const {DEALER_PACKS} = config.get('dealer')
  const URL_STATICS_IMAGES = config.get('URL_STATICS_IMAGES')
  const {FINANCING_PARTNERS, INSURANCE_IDS, LENDERS_DICTIONARIES} = config.get('financing')
  const {AD_DETAIL_PAGE_SECTION_IDS} = config.get('COMMON').AD_DETAIL_PAGE_SECTIONS

  const mode = resolveFinancingMode({
    dealerPacks: DEALER_PACKS,
    financingData,
    seller,
    showB2cFinancingPanel,
    showC2cFinancing
  })

  const {
    appliedEntry,
    calculationsErrorKey,
    cashPrice,
    currentFare,
    entryError,
    entryValue,
    fares,
    hasCommittedEntry,
    hasCtaError,
    isCtaLoading,
    isRefreshing,
    onCtaClick,
    onEntryCommit,
    onEntryChange,
    onFareChange,
    onTermsChange,
    selectedFare,
    selectedTerms,
    status,
    trackLegalContent
  } = useFinancingCalculations({ad, financingData, mode, vehicleInfo})

  /**
   * The single financing object both consumers read: the breakdown table below, and the price block
   * in `PriceInfo`.
   */
  const appliedFinancingData = useMemo(() => {
    if (!currentFare) return null

    return {
      ...financingData,
      ...currentFare,
      ...(cashPrice != null && {cashPrice}),
      ...(appliedEntry != null && {entry: appliedEntry}),
      ...(selectedTerms != null && {terms: selectedTerms})
    }
  }, [appliedEntry, cashPrice, currentFare, financingData, selectedTerms])

  useEffect(() => {
    if (appliedFinancingData) onFinancingDataChange?.(appliedFinancingData)
  }, [appliedFinancingData, onFinancingDataChange])

  if (!mode) return null

  const isReady = status === FINANCING_STATUS.READY && Boolean(currentFare)

  const {
    claimKeys,
    contactSellerCtaKey,
    ctaKey,
    entryFieldKey,
    feeLabelKey,
    isInteractive,
    showsFareSelector,
    titleKey,
    titleShortKey
  } = FINANCING_STRATEGIES[mode]

  // Unlike legacy, this panel has no server-rendered quote to fall back on, so a failed first
  // calculation leaves nothing to render but the reason why.
  if (status === FINANCING_STATUS.ERROR)
    return (
      <section className={BASE_CLASS} id={AD_DETAIL_PAGE_SECTION_IDS.FINANCING}>
        <Headline1>{i18n.t(titleShortKey)}</Headline1>
        <Body2 className={`${BASE_CLASS}-calculationsError`} role="alert">
          {i18n.t('COMMON.SERVICE_DOWN_TRY_LATER')}
        </Body2>
      </section>
    )

  const {lender} = financingData || {}
  const insuranceId = fares.find(({fareType}) => fareType === FARE_TYPES.WITH_INSURANCE)?.insuranceId
  const showContactSellerCta = Boolean(contactSellerCtaKey)

  const scrollToLegalConditions = () => {
    trackLegalContent(ad)
    scrollToSection(AD_DETAIL_PAGE_SECTION_IDS.FINANCING_LEGAL_CONDITIONS)
  }

  const handleContactSellerCtaClick = () => {
    track({
      event: 'Instalment Contact Button Clicked',
      data: {
        hit_origin: 'detail',
        products: [{id: ad?.id, category: 'ads', price: ad?.price, quantity: 0}]
      }
    })
    onOpenDrawer({type: DRAWER_TYPES.CONTACT_FORM})
    onContactSellerCtaClick?.({
      entry: appliedFinancingData.entry,
      fee: appliedFinancingData.fee,
      terms: appliedFinancingData.terms
    })
  }

  return (
    <section className={cx(BASE_CLASS, !isReady && `${BASE_CLASS}--loading`)} id={AD_DETAIL_PAGE_SECTION_IDS.FINANCING}>
      {isReady && (
        <>
          <div className={`${BASE_CLASS}-header`}>
            <Headline1>
              <span className={`${BASE_CLASS}-titleFull`}>{i18n.t(titleKey)}</span>
              <span className={`${BASE_CLASS}-titleShort`}>{i18n.t(titleShortKey)}</span>
            </Headline1>
            {lender && (
              <MediaImage
                alt={i18n.t(LENDERS_DICTIONARIES?.[lender]?.name)}
                className={`${BASE_CLASS}-lenderLogo`}
                loading={LOADING.LAZY}
                objectFit={OBJECT_FIT.CONTAIN}
                transparent
                src={`${URL_STATICS_IMAGES}common/logos/${lender}.svg`}
              />
            )}
          </div>

          {isInteractive && <FinancingClaims literalKeys={claimKeys} />}

          <div className={cx(`${BASE_CLASS}-content`, !isInteractive && `${BASE_CLASS}-content--simplified`)}>
            {isRefreshing && (
              <SpinnerBasic overlayType={spinnerBasicOverlayTypes.LIGHT} type={spinnerBasicTypes.SECTION} />
            )}
            <div className={`${BASE_CLASS}-calculator`}>
              {isInteractive ? (
                <>
                  <FinancingCalculator
                    appliedEntry={appliedEntry}
                    cashPrice={cashPrice}
                    currentFare={currentFare}
                    entryError={entryError}
                    entryFieldKey={entryFieldKey}
                    entryValue={entryValue}
                    fareLabelKey={feeLabelKey}
                    fares={fares}
                    financedPrice={financingData?.financedPrice}
                    hasCommittedEntry={hasCommittedEntry}
                    onEntryCommit={onEntryCommit}
                    onEntryChange={onEntryChange}
                    onFareSelect={onFareChange}
                    onInsuranceInfoClick={() => onOpenDrawer({data: insuranceId, type: DRAWER_TYPES.INSURANCE_INFO})}
                    onTermsChange={onTermsChange}
                    selectedFare={selectedFare}
                    selectedTerms={selectedTerms}
                    showsFareSelector={showsFareSelector}
                  />
                  {calculationsErrorKey && (
                    <Body2 className={`${BASE_CLASS}-calculationsError`} role="alert">
                      {i18n.t(calculationsErrorKey)}
                    </Body2>
                  )}
                </>
              ) : (
                <div className={`${BASE_CLASS}-simplifiedFee`}>
                  <Body1 highlight>{i18n.t(feeLabelKey)}</Body1>
                  {currentFare.fee != null && (
                    <span className={`${BASE_CLASS}-fareAmount`}>
                      <Headline1 as="span">{i18n.c(currentFare.fee)}</Headline1>
                      <Body2 highlight>/{i18n.t('COMMON.PLURALS.MONTH', 1).toLowerCase()}*</Body2>
                    </span>
                  )}
                </div>
              )}
            </div>

            <FinancingDetails
              legalLiteralKey={seller.isDealer ? 'FINANCING_INFO.SHOW_LEGAL_CONDITIONS' : 'SHOW_LEGAL_TERMS'}
              onLegalConditionsClick={scrollToLegalConditions}
              rows={getDetailRows({
                financingData: appliedFinancingData,
                financingPartners: FINANCING_PARTNERS,
                i18n,
                insuranceIds: INSURANCE_IDS
              })}
              showsTitle={isInteractive}
            />

            {isInteractive && (
              <div className={`${BASE_CLASS}-actions`}>
                <Button
                  disabled={Boolean(entryError)}
                  isLoading={isCtaLoading}
                  onClick={onCtaClick}
                  size={isHigherThanMobile ? BUTTON_SIZES.MEDIUM : BUTTON_SIZES.LARGE}
                  variant={BUTTON_VARIANTS.OUTLINE}
                  shape={BUTTON_SHAPES.CIRCLE}
                >
                  {i18n.t(ctaKey)}
                </Button>
                {hasCtaError && (
                  <Caption className={`${BASE_CLASS}-ctaError`} role="alert">
                    {i18n.t('PANEL_FINANCING.B2C.ERROR_MODAL_TEXT')}
                  </Caption>
                )}
              </div>
            )}

            {showContactSellerCta && (
              <div className={`${BASE_CLASS}-actions`}>
                <Button
                  onClick={handleContactSellerCtaClick}
                  size={isHigherThanMobile ? BUTTON_SIZES.MEDIUM : BUTTON_SIZES.LARGE}
                  variant={BUTTON_VARIANTS.OUTLINE}
                  shape={BUTTON_SHAPES.CIRCLE}
                >
                  {i18n.t(contactSellerCtaKey)}
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  )
}

FinancingPanel.displayName = 'FinancingPanel'

FinancingPanel.propTypes = {
  ad: PropTypes.shape({
    id: PropTypes.string,
    make: PropTypes.string,
    model: PropTypes.string,
    plateRegistrationDate: PropTypes.string,
    price: PropTypes.number,
    version: PropTypes.string,
    year: PropTypes.number
  }),
  financingData: PropTypes.shape({
    cashPrice: PropTypes.number,
    fee: PropTypes.number,
    financedPrice: PropTypes.number,
    id: PropTypes.string,
    lender: PropTypes.string,
    minEntry: PropTypes.number,
    terms: PropTypes.number
  }),
  onContactSellerCtaClick: PropTypes.func,
  onFinancingDataChange: PropTypes.func,
  onOpenDrawer: PropTypes.func.isRequired,
  seller: PropTypes.shape({
    dealer: PropTypes.shape({
      pack: PropTypes.string
    }),
    isDealer: PropTypes.bool
  }),
  showB2cFinancingPanel: PropTypes.bool,
  showC2cFinancing: PropTypes.bool,
  vehicleInfo: PropTypes.object
}

export default memo(FinancingPanel)
